import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock, Mail, User, Eye, EyeOff } from 'lucide-react';
// 🌟 FIX: Import your global shopping bag state hook
import { useCart } from '../context/CartContext'; 

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🌟 FIX: Pull the cloud synchronization trigger from your hook context
  const { triggerAuthSync } = useCart();
  
  // Toggle between Login view (true) and Signup view (false)
  const [isLoginView, setIsLoginView] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');

  // Form Fields State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (authError) setAuthError('');
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAuthError('');

    // Validation for Signup
    if (!isLoginView && formData.password !== formData.confirmPassword) {
      setAuthError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    const endpoint = isLoginView ? '/api/auth/login' : '/api/auth/register';
    const requestPayload = isLoginView 
      ? { email: formData.email, password: formData.password }
      : { name: formData.name, email: formData.email, password: formData.password };

    try {
      const response = await fetch(`https://api.modischekledings.com${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Authentication sequence failed.');
      }

      // Save credentials exactly how your Navbar & App.js expect to read them
      localStorage.setItem('modische_token', data.token);
      
      // 🌟 FIXED: Standardized storage payload structure with explicit absolute isAdmin validation mapping
      localStorage.setItem('modische_user', JSON.stringify({
        name: data.user.name,
        email: data.user.email,
        isAdmin: data.user.isAdmin === true || data.user.role === 'admin'
      }));

      // Fire the synchronization hook context instantly upon login success!
      if (triggerAuthSync) {
        triggerAuthSync();
      }

      // Smoothly redirect back to intended page (or homepage)
      const targetDestination = location.state?.from || '/';
      navigate(targetDestination, { replace: true });

    } catch (err) {
      console.error("Auth Engine Error:", err);
      setAuthError(err.message || 'Server connection timeout.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[85vh] bg-[#F5F5DC] pt-36 pb-20 px-4 flex flex-col justify-center items-center font-sans">
      
      {/* Dynamic Form Board Wrapper */}
      <div className="w-full max-w-md bg-[#E5D3B3] p-8 md:p-10 rounded-sm shadow-2xl border border-[#2C1E12]/10 relative">
        
        {/* Back navigation handle */}
        <button 
          type="button"
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 flex items-center gap-1.5 text-[8px] uppercase tracking-widest text-[#664229] hover:text-[#2C1E12] transition font-bold bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft size={10} /> Back To Gallery
        </button>

        {/* Brand Header Header Section */}
        <div className="text-center mb-8 border-b border-[#2C1E12]/10 pb-4 pt-4">
          <h2 className="text-2xl font-black tracking-tight text-[#2C1E12] uppercase">
            {isLoginView ? 'Customer Login' : 'Create Account'}
          </h2>
          <p className="text-[9px] uppercase tracking-widest text-[#664229] opacity-70 mt-0.5">
            {isLoginView ? 'Access your personal design lookbook' : 'Join Modische Kledings System'}
          </p>
        </div>

        {/* Primary Action Form */}
        <form onSubmit={handleAuthSubmit} className="space-y-5">
          
          {/* NAME FIELD (Only visible in signup layout mode) */}
          {!isLoginView && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Full Name</label>
              <div className="relative flex items-center">
                <User className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type="text" 
                  name="name"
                  required
                  placeholder="TARU PALIWAL"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3.5 pl-10 pr-4 text-xs font-mono uppercase tracking-wider text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
              </div>
            </div>
          )}

          {/* EMAIL INTERFACE INPUT */}
          <div className="space-y-1 text-left">
            <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Email Address</label>
            <div className="relative flex items-center">
              <Mail className="absolute left-3 w-4 h-4 text-[#664229]/50" />
              <input 
                type="email" 
                name="email"
                required
                placeholder="YOURNAME@GMAIL.COM"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3.5 pl-10 pr-4 text-xs font-mono uppercase tracking-wider text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
              />
            </div>
          </div>

          {/* PASSWORD INTERFACE INPUT */}
          <div className="space-y-1 text-left">
            <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Secure Password</label>
            <div className="relative flex items-center">
              <Lock className="absolute left-3 w-4 h-4 text-[#664229]/50" />
              <input 
                type={showPassword ? "text" : "password"} 
                name="password"
                required
                placeholder="••••••••"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3.5 pl-10 pr-10 text-xs font-mono text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 text-[#664229]/60 hover:text-[#2C1E12] bg-transparent border-none cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* CONFIRM PASSWORD INPUT (Only visible in signup layout mode) */}
          {!isLoginView && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Confirm Password</label>
              <div className="relative flex items-center">
                <Lock className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  name="confirmPassword"
                  required
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3.5 pl-10 pr-4 text-xs font-mono text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
              </div>
            </div>
          )}

          {/* Submit Action Control Switch */}
          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-[#2C1E12] text-[#F5F5DC] text-[10px] uppercase tracking-[0.3em] font-black hover:opacity-95 transition disabled:opacity-40 shadow-md mt-6 border-none cursor-pointer"
          >
            {isSubmitting ? 'Verifying Identity...' : isLoginView ? 'Authenticate Account' : 'Initialize Profile'}
          </button>

          {/* Dynamic Error Module Feedback Block */}
          {authError && (
            <p className="text-center text-xs text-red-800 font-bold tracking-wide animate-pulse bg-red-500/10 py-2 rounded-sm">
              ❌ {authError}
            </p>
          )}

          {/* Toggle View Link System */}
          <div className="text-center pt-4 border-t border-[#2C1E12]/10 mt-4">
            <button
              type="button"
              onClick={() => {
                setIsLoginView(!isLoginView);
                setAuthError('');
              }}
              className="text-[10px] uppercase tracking-widest font-black text-[#664229] hover:text-[#2C1E12] transition bg-transparent border-none cursor-pointer underline underline-offset-4"
            >
              {isLoginView ? "Don't have an account? Sign Up" : "Already registered? Login Here"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CustomerAuth;